<template>
  <div class="flex flex-col max-h-[70vh]">
    <!-- Header (fixed) -->
    <div class="text-center pb-4 border-b border-gray-200 flex-shrink-0">
      <div class="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </div>
      <h2 class="text-2xl font-semibold text-gray-900">
        Import Complete!
      </h2>
      <p class="mt-2 text-gray-600">
        Review the extracted information before continuing.
      </p>
    </div>

    <!-- Scrollable Content -->
    <div class="flex-1 overflow-y-auto py-4 space-y-6">
      <!-- Screenshots Preview -->
      <div v-if="screenshots?.desktop || screenshots?.mobile" class="grid grid-cols-2 gap-4">
        <div v-if="screenshots?.desktop" class="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
          <img :src="screenshots.desktop" alt="Desktop view" class="w-full h-auto max-h-48 object-cover object-top">
          <p class="text-xs text-gray-500 p-2 text-center">
            Desktop View
          </p>
        </div>
        <div v-if="screenshots?.mobile" class="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex flex-col items-center">
          <img :src="screenshots.mobile" alt="Mobile view" class="max-w-[120px] max-h-48 h-auto object-cover object-top">
          <p class="text-xs text-gray-500 p-2 text-center">
            Mobile View
          </p>
        </div>
      </div>

      <!-- Business Info Section -->
      <section class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Business Information
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
            <input
              v-model="localData.website_fields.business_name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Your business name">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <input
              v-model="localData.website_fields.tagline"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Your tagline or slogan">
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="localData.website_fields.description"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Brief description of your business" />
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Information
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              v-model="localData.website_fields.phone"
              type="tel"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="(555) 123-4567">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              v-model="localData.website_fields.email"
              type="email"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="contact@example.com">
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              v-model="localData.website_fields.address"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="123 Main Street">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              v-model="localData.website_fields.city"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="City">
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                v-model="localData.website_fields.state"
                type="text"
                maxlength="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 uppercase"
                placeholder="CA">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
              <input
                v-model="localData.website_fields.zip_code"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="12345">
            </div>
          </div>
        </div>
      </section>

      <!-- Services Section -->
      <section class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Services ({{ localData.services?.length || 0 }})
        </h3>
        <div v-if="localData.services?.length > 0" class="space-y-2 max-h-48 overflow-y-auto">
          <div
            v-for="(service, index) in localData.services"
            :key="index"
            class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
            <input
              v-model="service.name"
              type="text"
              class="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="Service name">
            <button
              type="button"
              class="text-red-500 hover:text-red-700 p-1"
              @click="removeService(index)">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 italic">
          No services detected. You can add them in the next step.
        </p>
      </section>

      <!-- Service Areas Section -->
      <section v-if="hasServiceAreas" class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Service Areas
        </h3>
        <div class="space-y-3">
          <div v-if="localData.service_areas?.cities?.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">Cities Served</label>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="(city, index) in localData.service_areas.cities"
                :key="index"
                class="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded-md text-sm">
                {{ city }}
                <button
                  type="button"
                  class="text-gray-400 hover:text-red-500"
                  @click="removeCity(index)">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            </div>
          </div>
          <div v-if="localData.service_areas?.regions?.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">Regions</label>
            <p class="text-sm text-gray-600">
              {{ localData.service_areas.regions.join(', ') }}
            </p>
          </div>
          <div v-if="localData.service_areas?.radius_description">
            <label class="block text-sm font-medium text-gray-700 mb-1">Coverage</label>
            <p class="text-sm text-gray-600">
              {{ localData.service_areas.radius_description }}
            </p>
          </div>
        </div>
      </section>

      <!-- Logo & Images Section -->
      <section v-if="hasImages" class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Extracted Images
        </h3>

        <div class="space-y-4">
          <!-- Logo -->
          <div v-if="logoUrl" class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <p class="text-xs text-gray-500 mb-1">
                Logo
              </p>
              <div class="w-24 h-24 rounded-lg border border-gray-200 bg-white p-2 flex items-center justify-center overflow-hidden">
                <img
                  :src="logoUrl"
                  alt="Detected logo"
                  class="max-w-full max-h-full object-contain"
                  @error="handleImageError($event, 'logo')">
              </div>
            </div>
            <div v-if="localData.images?.logo?.dominant_color" class="text-xs text-gray-500">
              <p>Dominant color:</p>
              <div class="flex items-center gap-2 mt-1">
                <div
                  class="w-6 h-6 rounded border border-gray-200"
                  :style="{ backgroundColor: localData.images.logo.dominant_color }" />
                <span>{{ localData.images.logo.dominant_color }}</span>
              </div>
            </div>
          </div>

          <!-- Portfolio Images -->
          <div v-if="portfolioImages.length > 0">
            <p class="text-xs text-gray-500 mb-2">
              Portfolio/Work Images ({{ portfolioImages.length }})
            </p>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="(img, index) in portfolioImages.slice(0, 6)"
                :key="index"
                class="w-20 h-20 rounded-lg border border-gray-200 bg-white overflow-hidden">
                <img
                  :src="getImageUrl(img)"
                  :alt="`Portfolio image ${index + 1}`"
                  class="w-full h-full object-cover"
                  @error="handleImageError($event, 'portfolio')">
              </div>
              <div
                v-if="portfolioImages.length > 6"
                class="w-20 h-20 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-sm text-gray-500">
                +{{ portfolioImages.length - 6 }}
              </div>
            </div>
          </div>

          <!-- Team Images -->
          <div v-if="teamImages.length > 0">
            <p class="text-xs text-gray-500 mb-2">
              Team Photos ({{ teamImages.length }})
            </p>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="(img, index) in teamImages.slice(0, 6)"
                :key="index"
                class="w-16 h-16 rounded-full border border-gray-200 bg-white overflow-hidden">
                <img
                  :src="getImageUrl(img)"
                  :alt="`Team photo ${index + 1}`"
                  class="w-full h-full object-cover"
                  @error="handleImageError($event, 'team')">
              </div>
              <div
                v-if="teamImages.length > 6"
                class="w-16 h-16 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                +{{ teamImages.length - 6 }}
              </div>
            </div>
          </div>

          <!-- Hero Images -->
          <div v-if="heroImages.length > 0">
            <p class="text-xs text-gray-500 mb-2">
              Hero/Banner Images ({{ heroImages.length }})
            </p>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="(img, index) in heroImages.slice(0, 4)"
                :key="index"
                class="w-32 h-20 rounded-lg border border-gray-200 bg-white overflow-hidden">
                <img
                  :src="getImageUrl(img)"
                  :alt="`Hero image ${index + 1}`"
                  class="w-full h-full object-cover"
                  @error="handleImageError($event, 'hero')">
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Style Section -->
      <section v-if="hasStyleData" class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          Detected Style
        </h3>
        <div class="space-y-4">
          <!-- Colors -->
          <div class="flex flex-wrap gap-4">
            <div v-if="localData.style_guide?.primary_color" class="text-center">
              <div
                class="w-12 h-12 rounded-lg border border-gray-200 shadow-sm cursor-pointer"
                :style="{ backgroundColor: localData.style_guide.primary_color }"
                :title="localData.style_guide.primary_color" />
              <p class="text-xs text-gray-500 mt-1">
                Primary
              </p>
              <p class="text-xs text-gray-400">
                {{ localData.style_guide.primary_color }}
              </p>
            </div>
            <div v-if="localData.style_guide?.secondary_color" class="text-center">
              <div
                class="w-12 h-12 rounded-lg border border-gray-200 shadow-sm"
                :style="{ backgroundColor: localData.style_guide.secondary_color }"
                :title="localData.style_guide.secondary_color" />
              <p class="text-xs text-gray-500 mt-1">
                Secondary
              </p>
              <p class="text-xs text-gray-400">
                {{ localData.style_guide.secondary_color }}
              </p>
            </div>
            <div v-if="localData.style_guide?.accent_color" class="text-center">
              <div
                class="w-12 h-12 rounded-lg border border-gray-200 shadow-sm"
                :style="{ backgroundColor: localData.style_guide.accent_color }"
                :title="localData.style_guide.accent_color" />
              <p class="text-xs text-gray-500 mt-1">
                Accent
              </p>
              <p class="text-xs text-gray-400">
                {{ localData.style_guide.accent_color }}
              </p>
            </div>
          </div>

          <!-- Color Palette -->
          <div v-if="localData.style_guide?.color_palette?.length > 0">
            <p class="text-xs text-gray-500 mb-2">
              Full Color Palette
            </p>
            <div class="flex flex-wrap gap-1">
              <div
                v-for="(color, index) in localData.style_guide.color_palette"
                :key="index"
                class="w-8 h-8 rounded border border-gray-200"
                :style="{ backgroundColor: color }"
                :title="color" />
            </div>
          </div>

          <!-- Fonts -->
          <div class="flex flex-wrap gap-6">
            <div v-if="localData.style_guide?.heading_font">
              <p class="text-xs text-gray-500">
                Heading Font
              </p>
              <p class="text-sm font-medium">
                {{ localData.style_guide.heading_font }}
              </p>
            </div>
            <div v-if="localData.style_guide?.body_font">
              <p class="text-xs text-gray-500">
                Body Font
              </p>
              <p class="text-sm font-medium">
                {{ localData.style_guide.body_font }}
              </p>
            </div>
            <div v-if="localData.style_guide?.style_preset">
              <p class="text-xs text-gray-500">
                Style
              </p>
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 capitalize">
                {{ localData.style_guide.style_preset }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Social Links Section -->
      <section v-if="hasSocialLinks" class="bg-gray-50 rounded-lg p-4">
        <h3 class="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          Social Links
        </h3>
        <div class="flex flex-wrap gap-3">
          <a
            v-if="localData.social_links?.facebook"
            :href="localData.social_links.facebook"
            target="_blank"
            class="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
            Facebook
          </a>
          <a
            v-if="localData.social_links?.instagram"
            :href="localData.social_links.instagram"
            target="_blank"
            class="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
            Instagram
          </a>
          <a
            v-if="localData.social_links?.twitter"
            :href="localData.social_links.twitter"
            target="_blank"
            class="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            Twitter/X
          </a>
          <a
            v-if="localData.social_links?.linkedin"
            :href="localData.social_links.linkedin"
            target="_blank"
            class="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            LinkedIn
          </a>
          <a
            v-if="localData.social_links?.youtube"
            :href="localData.social_links.youtube"
            target="_blank"
            class="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
            YouTube
          </a>
          <a
            v-if="localData.social_links?.yelp"
            :href="localData.social_links.yelp"
            target="_blank"
            class="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.905-4.308a1.072 1.072 0 011.596-.206 9.194 9.194 0 011.85 2.564c.336.72-.057 1.5-.18 2.147zm-7.842 3.593l1.03 5.057c.194.94-.9 1.65-1.65 1.1l-3.88-2.86a1.072 1.072 0 01-.276-1.572 9.19 9.19 0 012.35-2.3c.65-.468 1.567.01 2.426.576zm-4.542-2.342l-5.063 1.03c-.95.195-1.66-.9-1.1-1.65l2.86-3.88a1.072 1.072 0 011.573-.276 9.19 9.19 0 012.298 2.35c.47.65-.01 1.567-.568 2.426zm1.54-4.594L6.49 4.234c-.555-.75.16-1.7 1.1-1.5l4.88 1.03c.76.16 1.14.94.85 1.62-.72 1.7-1.85 3.14-3.04 4.2-.47.42-1.17.25-1.5-.333z" /></svg>
            Yelp
          </a>
        </div>
      </section>
    </div>

    <!-- Actions (fixed) -->
    <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 flex-shrink-0">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        @click="$emit('cancel')">
        Cancel
      </button>
      <button
        type="button"
        class="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        :disabled="applying"
        @click="handleApply">
        <svg v-if="applying" class="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ applying ? 'Applying...' : 'Apply & Continue' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  screenshots: { type: Object, default: () => ({ desktop: null, mobile: null }) },
  applying: { type: Boolean, default: false }
})

const emit = defineEmits(['apply', 'cancel'])

// Create a local copy for editing with safe defaults
const localData = ref({
  website_fields: {
    business_name: '',
    tagline: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    ...props.data?.website_fields
  },
  services: props.data?.services || [],
  service_areas: props.data?.service_areas || { cities: [], regions: [], zip_codes: []},
  style_guide: props.data?.style_guide || {},
  social_links: props.data?.social_links || {},
  images: props.data?.images || { logo: null, portfolio: [], team: [], hero_images: []}
})

onMounted(() => {
  console.log('[ReviewModal] Received data:', props.data)
  console.log('[ReviewModal] Screenshots:', props.screenshots)
  console.log('[ReviewModal] Images:', props.data?.images)
  console.log('[ReviewModal] Logo:', props.data?.images?.logo)
})

const hasStyleData = computed(() => {
  const style = localData.value.style_guide
  return style && (style.primary_color || style.secondary_color || style.accent_color || style.heading_font || style.body_font || style.color_palette?.length > 0)
})

const hasServiceAreas = computed(() => {
  const areas = localData.value.service_areas
  return areas && (areas.cities?.length > 0 || areas.regions?.length > 0 || areas.radius_description)
})

const hasSocialLinks = computed(() => {
  const links = localData.value.social_links
  return links && (links.facebook || links.instagram || links.twitter || links.linkedin || links.youtube || links.yelp)
})

// Images computed properties
const hasImages = computed(() => {
  const images = localData.value.images
  return images && (
    images.logo?.blob_signed_id ||
    images.logo?.url ||
    images.portfolio?.length > 0 ||
    images.team?.length > 0 ||
    images.hero_images?.length > 0
  )
})

const logoUrl = computed(() => {
  const logo = localData.value.images?.logo
  if (!logo) return null
  // Prefer blob_signed_id (downloaded), fall back to url
  if (logo.blob_signed_id) {
    return `/rails/active_storage/blobs/${logo.blob_signed_id}/logo`
  }
  return logo.url || null
})

const portfolioImages = computed(() => localData.value.images?.portfolio || [])

const teamImages = computed(() => localData.value.images?.team || [])

const heroImages = computed(() => localData.value.images?.hero_images || [])

// Helper to get URL from image object (blob_signed_id or url)
function getImageUrl(img) {
  if (!img) return null
  if (img.blob_signed_id) {
    return `/rails/active_storage/blobs/${img.blob_signed_id}/image`
  }
  return img.url || img
}

// Handle image load errors gracefully
function handleImageError(event, category) {
  console.warn(`[ReviewModal] Failed to load ${category} image:`, event.target.src)
  // Hide the broken image
  event.target.style.display = 'none'
}

function removeService(index) {
  localData.value.services.splice(index, 1)
}

function removeCity(index) {
  localData.value.service_areas.cities.splice(index, 1)
}

function handleApply() {
  emit('apply', localData.value)
}
</script>
